import React, { useEffect, useState } from "react";

export const PUB_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=DM+Sans:wght@300;400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --navy:    #080d1a;
  --navy2:   #0d1526;
  --navy3:   #111e35;
  --border:  rgba(255,255,255,.07);
  --gold:    #c9a84c;
  --goldl:   #e2c47a;
  --golddim: rgba(201,168,76,.08);
  --text:    #f0ede8;
  --muted:   #6b7280;
  --dim:     #374151;
  --green:   #4ade80;
  --red:     #f87171;
}

html, body { height: 100%; overflow-x: hidden; }
body { background: var(--navy); color: var(--text); font-family: 'DM Sans', sans-serif; }

/* ── NAV ── */
.pub-nav {
  position: sticky; top: 0; z-index: 100;
  background: rgba(6,10,20,.95); backdrop-filter: blur(24px);
  border-bottom: 1px solid var(--border);
  padding: 0 60px; height: 72px;
  display: flex; align-items: center; justify-content: space-between;
}
.pub-nav-logo {
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px; font-weight: 600; letter-spacing: 1px; cursor: pointer;
  color: var(--text); text-decoration: none;
}
.pub-nav-logo span { color: var(--gold); }
.pub-nav-links { display: flex; gap: 4px; align-items: center; }
.pub-nav-link {
  padding: 8px 16px; border-radius: 100px;
  border: 1px solid transparent; background: transparent;
  color: var(--muted); font-size: 13px; font-family: 'DM Sans', sans-serif;
  cursor: pointer; transition: all .2s; text-decoration: none;
}
.pub-nav-link:hover { color: var(--text); border-color: var(--border); }
.pub-nav-cta {
  margin-left: 8px; padding: 9px 22px; border-radius: 100px;
  background: var(--gold); border: none; color: #080d1a;
  font-size: 13px; font-weight: 600; font-family: 'DM Sans', sans-serif;
  cursor: pointer; transition: opacity .2s; text-decoration: none;
}
.pub-nav-cta:hover { opacity: .88; }

/* ── COMMON ── */
.pub-page { min-height: 100vh; background: var(--navy); }
.pub-eyebrow { font-size: 11px; letter-spacing: 3px; text-transform: uppercase; color: var(--gold); margin-bottom: 12px; }
.pub-h1 {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(36px, 5vw, 64px); font-weight: 300; line-height: 1.12; margin-bottom: 20px;
}
.pub-h2 {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(28px, 3.5vw, 48px); font-weight: 300; line-height: 1.2; margin-bottom: 16px;
}
.pub-p { font-size: 15px; color: var(--muted); line-height: 1.85; }
.pub-wrap { max-width: 1200px; margin: 0 auto; padding: 0 60px; }
.pub-section { padding: 80px 0; }
.pub-dark { background: rgba(4,8,16,.7); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }

/* ── GRID LAYOUTS ── */
.pub-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
.pub-grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; }
.pub-grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; }

/* ── CARDS ── */
.pub-card {
  background: var(--navy2); border: 1px solid var(--border);
  border-radius: 20px; padding: 32px; transition: all .3s;
}
.pub-card:hover { border-color: rgba(201,168,76,.3); transform: translateY(-4px); }
.pub-card-icon { font-size: 32px; margin-bottom: 16px; }
.pub-card-title { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 400; margin-bottom: 10px; }
.pub-card-text { font-size: 13px; color: var(--muted); line-height: 1.75; }

/* ── STAT ROW ── */
.pub-stat-row {
  display: grid; grid-template-columns: repeat(4,1fr);
  gap: 1px; background: var(--border); border-radius: 20px; overflow: hidden; margin: 48px 0;
}
.pub-stat-cell { background: var(--navy2); padding: 32px; text-align: center; }
.pub-stat-val { font-family: 'Cormorant Garamond', serif; font-size: 44px; font-weight: 300; color: var(--gold); line-height: 1; }
.pub-stat-lbl { font-size: 12px; color: var(--muted); margin-top: 8px; letter-spacing: .5px; }

/* ── IMAGE ── */
.pub-img { border-radius: 20px; overflow: hidden; }
.pub-img img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .6s ease; }
.pub-img:hover img { transform: scale(1.04); }

/* ── BUTTONS ── */
.pub-btn-gold {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 13px 28px; border-radius: 100px;
  background: var(--gold); border: none; color: #080d1a;
  font-size: 14px; font-weight: 600; font-family: 'DM Sans', sans-serif;
  cursor: pointer; transition: opacity .2s; text-decoration: none;
}
.pub-btn-gold:hover { opacity: .88; }
.pub-btn-ghost {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 13px 28px; border-radius: 100px;
  background: transparent; border: 1px solid var(--border); color: var(--text);
  font-size: 14px; font-family: 'DM Sans', sans-serif;
  cursor: pointer; transition: all .2s; text-decoration: none;
}
.pub-btn-ghost:hover { border-color: rgba(201,168,76,.4); color: var(--gold); }

/* ── CTA BANNER ── */
.pub-cta-banner {
  background: var(--golddim); border-top: 1px solid rgba(201,168,76,.15);
  padding: 72px 60px; text-align: center;
}
.pub-cta-banner h2 { font-family: 'Cormorant Garamond', serif; font-size: 40px; font-weight: 300; margin-bottom: 12px; }
.pub-cta-banner p { font-size: 15px; color: var(--muted); margin-bottom: 32px; }
.pub-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

/* ── FOOTER ── */
.pub-footer { background: rgba(3,6,14,.99); border-top: 1px solid var(--border); padding: 72px 60px 32px; }
.pub-footer-grid {
  max-width: 1200px; margin: 0 auto;
  display: grid; grid-template-columns: 2.4fr 1fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 56px;
}
.pub-footer-logo { font-family: 'Cormorant Garamond', serif; font-size: 20px; letter-spacing: 1px; display: block; margin-bottom: 14px; }
.pub-footer-logo span { color: var(--gold); }
.pub-footer-desc { font-size: 13px; color: var(--muted); line-height: 1.9; max-width: 256px; margin-bottom: 22px; }
.pub-footer-contact { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 14px; }
.pub-footer-contact-icon { font-size: 14px; margin-top: 1px; flex-shrink: 0; }
.pub-footer-contact-text { font-size: 12px; color: var(--muted); line-height: 1.7; }
.pub-footer-contact-text a { color: var(--gold); text-decoration: none; }
.pub-footer-contact-text a:hover { text-decoration: underline; }
.pub-footer-social { display: flex; gap: 8px; margin-top: 20px; }
.pub-footer-social-btn {
  width: 36px; height: 36px; border-radius: 10px;
  border: 1px solid var(--border); background: transparent;
  color: var(--muted); font-size: 13px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: all .2s;
}
.pub-footer-social-btn:hover { border-color: var(--gold); color: var(--gold); background: var(--golddim); }
.pub-footer-col h4 { font-size: 10px; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold); margin-bottom: 18px; }
.pub-footer-col a { display: block; font-size: 13px; color: var(--muted); margin-bottom: 12px; cursor: pointer; text-decoration: none; transition: color .2s; }
.pub-footer-col a:hover { color: var(--text); }
.pub-footer-bottom {
  max-width: 1200px; margin: 0 auto; border-top: 1px solid var(--border);
  padding-top: 28px; display: flex; justify-content: space-between;
  align-items: center; flex-wrap: wrap; gap: 16px; font-size: 12px; color: var(--dim);
}
.pub-footer-badges { display: flex; gap: 8px; flex-wrap: wrap; }
.pub-footer-badge {
  font-size: 10px; color: var(--muted); padding: 4px 12px;
  border: 1px solid var(--border); border-radius: 100px;
  display: flex; align-items: center; gap: 4px;
}
.pub-footer-badge span { color: var(--green); }

/* ── ANIMATIONS ── */
@keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
.anim-up { animation: fadeUp .6s ease forwards; }

/* ── HAMBURGER ── */
.pub-ham-btn {
  display: none; flex-direction: column; justify-content: center; gap: 5px;
  background: none; border: 1px solid var(--border); border-radius: 10px;
  padding: 8px 10px; cursor: pointer; transition: border-color .2s; flex-shrink: 0;
}
.pub-ham-btn:hover { border-color: var(--gold); }
.pub-ham-line {
  width: 18px; height: 1.5px; background: var(--muted);
  border-radius: 2px; transition: all .25s; display: block;
}
.pub-ham-btn.open .pub-ham-line:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
.pub-ham-btn.open .pub-ham-line:nth-child(2) { opacity: 0; transform: scaleX(0); }
.pub-ham-btn.open .pub-ham-line:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }
.pub-mobile-menu {
  display: none; position: fixed; top: 72px; left: 0; right: 0;
  background: rgba(6,10,20,.98); backdrop-filter: blur(24px);
  border-bottom: 1px solid var(--border);
  flex-direction: column; gap: 0; z-index: 99;
  padding: 8px 0 16px;
}
.pub-mobile-menu.open { display: flex; }
.pub-mobile-link {
  background: none; border: none; color: var(--muted);
  font-size: 15px; font-family: 'DM Sans', sans-serif;
  padding: 14px 24px; cursor: pointer; text-align: left;
  border-bottom: 1px solid var(--border); transition: color .2s; width: 100%;
}
.pub-mobile-link:last-child { border-bottom: none; }
.pub-mobile-link:hover { color: var(--gold); }
.pub-mobile-link.active { color: var(--text); }
.pub-mobile-cta {
  background: var(--gold); border: none; color: #080d1a;
  font-size: 14px; font-weight: 600; font-family: 'DM Sans', sans-serif;
  padding: 14px 24px; cursor: pointer; text-align: left;
  margin: 8px 16px 0; border-radius: 100px; width: calc(100% - 32px);
  transition: opacity .2s;
}
.pub-mobile-cta:hover { opacity: .88; }

/* ── RESPONSIVE ── */
@media (max-width: 960px) {
  .pub-nav { padding: 0 24px; }
  .pub-wrap { padding: 0 24px; }
  .pub-section { padding: 56px 0; }
  .pub-grid-2, .pub-grid-3, .pub-grid-4 { grid-template-columns: 1fr; }
  .pub-stat-row { grid-template-columns: 1fr 1fr; }
  .pub-footer { padding: 48px 24px 24px; }
  .pub-footer-grid { grid-template-columns: 1fr 1fr; gap: 32px; }
  .pub-cta-banner { padding: 56px 24px; }
}
@media (max-width: 640px) {
  .pub-ham-btn { display: flex; }
  .pub-nav-links { display: none; }
  .pub-stat-row { grid-template-columns: 1fr 1fr; }
  .pub-footer-grid { grid-template-columns: 1fr; }
  .pub-grid-4 { grid-template-columns: 1fr 1fr; }
}
@media (min-width: 641px) {
  .pub-mobile-menu { display: none !important; }
}
`;

export function injectPubStyles() {
  if (document.getElementById('pub-global-styles')) return;
  const s = document.createElement('style');
  s.id = 'pub-global-styles';
  s.textContent = PUB_STYLES;
  document.head.appendChild(s);
}

// ── Types ──
export type PubPage = 'landing' | 'about' | 'contact' | 'portfolio';

interface PubNavProps {
  current?: PubPage;
  onNavigate: (page: PubPage) => void;
}

// ── Public Nav ──
export function PubNav({ current, onNavigate }: PubNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);
  const go = (p: PubPage) => { onNavigate(p); close(); };

  // Close menu on resize to desktop
  useEffect(() => {
    const handler = () => { if (window.innerWidth >= 641) close(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <>
      <nav className="pub-nav">
        <span className="pub-nav-logo" onClick={() => go('landing')}>
          NATIONSTAR<span> Bank</span>
        </span>
        {/* Desktop links */}
        <div className="pub-nav-links">
          {(['about', 'portfolio', 'contact'] as PubPage[]).map(p => (
            <button
              key={p}
              className="pub-nav-link"
              style={current === p ? { color: 'var(--text)', borderColor: 'var(--border)' } : {}}
              onClick={() => go(p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
          <button className="pub-nav-cta" onClick={() => go('landing')}>Sign In</button>
        </div>
        {/* Hamburger button — mobile only */}
        <button
          className={`pub-ham-btn${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Menu"
        >
          <span className="pub-ham-line" />
          <span className="pub-ham-line" />
          <span className="pub-ham-line" />
        </button>
      </nav>
      {/* Mobile dropdown */}
      <div className={`pub-mobile-menu${menuOpen ? ' open' : ''}`}>
        {(['about', 'portfolio', 'contact'] as PubPage[]).map(p => (
          <button
            key={p}
            className={`pub-mobile-link${current === p ? ' active' : ''}`}
            onClick={() => go(p)}
          >
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
        <button className="pub-mobile-cta" onClick={() => go('landing')}>Sign In →</button>
      </div>
    </>
  );
}

// ── Public Footer ──
interface PubFooterProps {
  onNavigate: (page: PubPage) => void;
}

export function PubFooter({ onNavigate }: PubFooterProps) {
  return (
    <footer className="pub-footer">
      <div className="pub-footer-grid">
        {/* Brand */}
        <div>
          <span className="pub-footer-logo">NATIONSTAR<span> Bank</span></span>
          <p className="pub-footer-desc">
            A premier digital banking institution committed to world-class financial
            services with transparency, integrity, and innovation since 1998.
          </p>
          {[
            { icon: '✉', content: <><a href="mailto:info@nationstardigitalbank.com">info@nationstardigitalbank.com</a><br /><small style={{ color: 'var(--dim)' }}>General Enquiries</small></> },
            { icon: '🛡', content: <><a href="mailto:support@nationstardigitalbank.com">support@nationstardigitalbank.com</a><br /><small style={{ color: 'var(--dim)' }}>Customer Support · 24/7</small></> },
            { icon: '📞', content: <>1-800-NSTAR-01<br /><small style={{ color: 'var(--dim)' }}>Mon–Fri, 8am–8pm EST</small></> },
          ].map(({ icon, content }, i) => (
            <div key={i} className="pub-footer-contact">
              <span className="pub-footer-contact-icon">{icon}</span>
              <div className="pub-footer-contact-text">{content}</div>
            </div>
          ))}
          <div className="pub-footer-social">
            {['𝕏', 'in', 'f', '▶'].map(s => (
              <button key={s} className="pub-footer-social-btn">{s}</button>
            ))}
          </div>
        </div>

        {/* Company */}
        <div className="pub-footer-col">
          <h4>Company</h4>
          <a onClick={() => onNavigate('about')}>About Us</a>
          <a onClick={() => onNavigate('about')}>Leadership</a>
          <a onClick={() => onNavigate('portfolio')}>Portfolio</a>
          <a onClick={() => onNavigate('about')}>Press & Media</a>
          <a onClick={() => onNavigate('about')}>Careers</a>
          <a onClick={() => onNavigate('contact')}>Investors</a>
        </div>

        {/* Services */}
        <div className="pub-footer-col">
          <h4>Services</h4>
          <a onClick={() => onNavigate('landing')}>Personal Banking</a>
          <a onClick={() => onNavigate('landing')}>Business Accounts</a>
          <a onClick={() => onNavigate('landing')}>Investments</a>
          <a onClick={() => onNavigate('landing')}>Loans & Credit</a>
          <a onClick={() => onNavigate('landing')}>Savings Goals</a>
          <a onClick={() => onNavigate('landing')}>Wire Transfers</a>
        </div>

        {/* Legal */}
        <div className="pub-footer-col">
          <h4>Legal</h4>
          {['Privacy Policy', 'Terms of Service', 'Cookie Policy', 'AML Policy', 'FDIC Disclosures', 'Accessibility'].map(l => (
            <a key={l}>{l}</a>
          ))}
        </div>

        {/* Support */}
        <div className="pub-footer-col">
          <h4>Support</h4>
          <a onClick={() => onNavigate('contact')}>Help Center</a>
          <a onClick={() => onNavigate('contact')}>Contact Us</a>
          <a onClick={() => onNavigate('contact')}>Report Fraud</a>
          <a>Security Tips</a>
          <a>Fee Schedule</a>
          <a>Find a Branch</a>
        </div>
      </div>

      <div className="pub-footer-bottom">
        <div>© {new Date().getFullYear()} Nationstar Digital Bank N.A. · All rights reserved · Member FDIC</div>
        <div className="pub-footer-badges">
          {[['✓','FDIC Insured'],['✓','256-bit SSL'],['✓','SOC 2 Type II'],['✓','Reg. FCA'],['✓','PCI DSS']].map(([icon, label]) => (
            <div key={label} className="pub-footer-badge"><span>{icon}</span>{label}</div>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ── Hook to inject styles ──
export function usePubStyles() {
  useEffect(() => { injectPubStyles(); }, []);
}
